import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = '';
const C = {
  green: '#3B6D11', lgreen: '#EAF3DE',
  red: '#A32D2D', lred: '#FCEBEB',
  blue: '#185FA5', lblue: '#E6F1FB',
  gray: '#5F5E5A', line: '#E1E0D9',
};

const fmtWhen = ts => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short',
    year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

const Pill = ({ text, ok }) => (
  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10,
    background: ok ? C.lgreen : C.lred, color: ok ? C.green : C.red }}>{text}</span>
);

const BrokerUploadPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const refs = useRef({});

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/broker/status`),
        axios.get(`${API}/broker/history`),
      ]);
      setAccounts(s.data.accounts || []);
      setHistory(h.data.rows || []);
    } catch (e) { /* leave prior state */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key); setMsg(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('broker', key);
    try {
      const r = await axios.post(`${API}/broker/upload`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data;
      setMsg({ ok: true, text: `${d.filename}: ${d.inserted} new leg(s), `
        + `${d.already_present} already present — ${d.from_date} to ${d.to_date}` });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || String(e) });
    }
    setBusy(null);
    if (refs.current[key]) refs.current[key].value = '';
  };

  const th = { textAlign: 'left', padding: '8px 6px', fontSize: 11,
    color: C.gray, textTransform: 'uppercase', letterSpacing: 0.3,
    borderBottom: `1px solid ${C.line}` };
  const td = { padding: '10px 6px', fontSize: 13, borderBottom: `1px solid ${C.line}` };

  return (
    <div style={{ background: 'var(--bg1)', border: `1px solid ${C.line}`,
                  borderRadius: 10, padding: 20, marginTop: 24 }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
        Broker Tradebooks
      </div>
      <div style={{ fontSize: 12, color: C.gray, marginBottom: 16 }}>
        Upload the settled tradebook the morning after trading. Loaders are
        idempotent — re-uploading a wider date range only fills in what is missing.
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>Account</th>
          <th style={th}>Data From</th>
          <th style={th}>To</th>
          <th style={{ ...th, textAlign: 'right' }}>Legs</th>
          <th style={{ ...th, textAlign: 'right' }}>Tickers</th>
          <th style={{ ...th, textAlign: 'right' }}>Open</th>
          <th style={th}>Last Upload</th>
          <th style={{ ...th, textAlign: 'right' }}>Upload</th>
        </tr></thead>
        <tbody>
          {accounts.map(a => (
            <tr key={a.key}>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{a.label}</div>
                <div style={{ fontSize: 11, color: C.gray }}>{a.hint}</div>
              </td>
              <td style={td}>{a.from_date || '—'}</td>
              <td style={td}>{a.to_date || '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>{a.legs}</td>
              <td style={{ ...td, textAlign: 'right' }}>{a.tickers}</td>
              <td style={{ ...td, textAlign: 'right' }}>{a.open_positions}</td>
              <td style={{ ...td, fontSize: 12 }}>{fmtWhen(a.last_upload)}</td>
              <td style={{ ...td, textAlign: 'right' }}>
                <input ref={el => (refs.current[a.key] = el)} type="file"
                       accept=".xlsx" style={{ display: 'none' }}
                       onChange={e => upload(a.key, e.target.files[0])} />
                <button disabled={busy === a.key}
                        onClick={() => refs.current[a.key]?.click()}
                        style={{ fontSize: 12, padding: '5px 14px', borderRadius: 6,
                          border: `1px solid ${C.blue}`, background: C.lblue,
                          color: C.blue, cursor: busy === a.key ? 'wait' : 'pointer' }}>
                  {busy === a.key ? 'Loading…' : 'Choose file'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {msg && (
        <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 6,
          fontSize: 12, fontWeight: 500,
          background: msg.ok ? C.lgreen : C.lred,
          color: msg.ok ? C.green : C.red }}>
          {msg.ok ? '\u2713 ' : '\u2717 '}{msg.text}
        </div>
      )}

      {history.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 24, marginBottom: 8 }}>
            Recent uploads
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>When</th><th style={th}>Account</th>
              <th style={th}>Source</th>
              <th style={{ ...th, textAlign: 'right' }}>Legs</th>
              <th style={th}>Covering</th>
            </tr></thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontSize: 12 }}>{fmtWhen(h.uploaded_at)}</td>
                  <td style={td}><Pill text={h.account} ok={h.account === 'PL'} /></td>
                  <td style={{ ...td, fontSize: 12, color: C.gray }}>{h.source_file}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{h.legs}</td>
                  <td style={{ ...td, fontSize: 12 }}>{h.from_date} → {h.to_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default BrokerUploadPanel;
